/**
 * BankFeedReconciliationTab - extracted from BankFeedCenterPage Phase 2.
 * Renders the per-account reconciliation summary cards and embeds the
 * existing <BankReconciliationCenter> when a bank account is selected.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Scale, Search, Building2, FileText } from 'lucide-react';
import BankReconciliationCenter from './BankReconciliationCenter';
import StatementVaultDrawer from '../banking/StatementVaultDrawer';

export const BankFeedReconciliationTab = ({
  bankAccounts = [],
  business,
  reconBankAccountId,
  setReconBankAccountId,
  reconSummary,
  formatAmount,
  fetchReconSummary,
}) => {
  const [vaultOpen, setVaultOpen] = useState(false);
  const selectedAccount = bankAccounts.find((b) => b.id === reconBankAccountId) || null;
  const labelFor = (ba) => {
    const bank = (ba.bank_name || '').trim();
    const name = (ba.account_name || '').trim();
    const num = String(ba.mask || ba.account_number || '');
    const last4 = /^\d{4}$/.test(num.slice(-4)) ? num.slice(-4) : null;
    const parts = [bank];
    if (name && name.toLowerCase() !== bank.toLowerCase() && !(last4 && name.endsWith(last4) && /^(current ?account|account)?$/i.test(name.replace(last4, '').trim()))) parts.push(name);
    return `${parts.filter(Boolean).join(' · ')}${last4 ? ` ···${last4}` : ''}`;
  };
  return (
    <>
      {/* Bank Account Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Scale className="w-5 h-5 text-violet-600" />
            Bank Reconciliation
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">Select a bank account to reconcile</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Select value={reconBankAccountId} onValueChange={setReconBankAccountId}>
            <SelectTrigger className="w-full sm:w-72" data-testid="recon-bank-selector">
              <SelectValue placeholder="Select bank account..." />
            </SelectTrigger>
            <SelectContent>
              {bankAccounts.map(ba => (
                <SelectItem key={ba.id} value={ba.id}>
                  {labelFor(ba)}{ba.currency ? ` · ${ba.currency}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedAccount && (
            <button
              type="button"
              onClick={() => setVaultOpen(true)}
              data-testid="recon-statements-btn"
              className="shrink-0 text-xs font-medium text-slate-700 border border-slate-200 hover:bg-slate-100 rounded-md px-2.5 py-2 flex items-center gap-1.5"
              title="View and download the bank statements on file for this account"
            >
              <FileText className="w-3.5 h-3.5" /> Statements
            </button>
          )}
        </div>
      </div>
      {selectedAccount && (
        <StatementVaultDrawer open={vaultOpen} onClose={() => setVaultOpen(false)} bankAccount={selectedAccount} />
      )}

      {/* Summary cards for all accounts (shown when no account selected) */}
      {reconSummary && reconSummary.accounts && reconSummary.accounts.length > 0 && !reconBankAccountId && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reconSummary.accounts.map(acc => (
            <Card
              key={acc.bank_account_id}
              className={`cursor-pointer hover:shadow-md transition-all ${acc.is_reconciled ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}
              onClick={() => setReconBankAccountId(acc.bank_account_id)}
              data-testid={`recon-account-${acc.bank_account_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <h4 className="font-semibold text-sm leading-tight" data-testid={`recon-card-label-${acc.bank_account_id}`}>
                      {acc.account_label || `${acc.bank_name} · ${acc.account_name}`}
                    </h4>
                    {acc.currency && <span className="text-[11px] text-slate-500">{acc.currency}</span>}
                  </div>
                  <Badge variant={acc.is_reconciled ? 'default' : 'secondary'} className={acc.is_reconciled ? 'bg-emerald-600 shrink-0' : 'shrink-0 bg-white border text-slate-700'}>
                    {acc.is_reconciled ? 'Reconciled' : `Difference ${formatAmount(Math.abs(acc.variance), acc.currency)}`}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center text-xs mt-3">
                  <div className="bg-white rounded p-2 border">
                    <span className="block font-bold text-blue-700" data-testid={`recon-card-in-bank-${acc.bank_account_id}`}>{acc.unmatched_bank}</span>
                    <span className="text-slate-500">Bank items to review</span>
                  </div>
                  <div className="bg-white rounded p-2 border">
                    <span className="block font-bold text-amber-700" data-testid={`recon-card-in-books-${acc.bank_account_id}`}>{acc.unmatched_books}</span>
                    <span className="text-slate-500">Book entries awaiting bank</span>
                  </div>
                </div>
                {acc.suggested_count > 0 && (
                  <div className="mt-2 text-xs text-blue-600 flex items-center gap-1">
                    <Search className="w-3 h-3" />
                    {acc.suggested_count} suggested match{acc.suggested_count > 1 ? 'es' : ''}
                  </div>
                )}
                {acc.is_reconciled === false && (acc.unmatched_bank + acc.unmatched_books) === 0 && (
                  <p className="mt-2 text-[11px] text-slate-500">Open to see what explains the difference</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* No accounts message */}
      {(!bankAccounts || bankAccounts.length === 0) && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-slate-400">
            <Building2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No bank accounts found</p>
            <p className="text-sm mt-1">Add a bank account to start reconciliation</p>
          </CardContent>
        </Card>
      )}

      {/* Reconciliation Action Center for selected account */}
      {reconBankAccountId && (
        <BankReconciliationCenter
          bankAccountId={reconBankAccountId}
          onClose={() => setReconBankAccountId('')}
          onRefresh={fetchReconSummary}
        />
      )}
    </>
  );
};

export default BankFeedReconciliationTab;
